import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import checkIcon from './icons/check.svg';
import { cn } from '@/lib/cn';

export interface DropdownMenuItem<T = ReactNode | string> {
  icon?: string;
  label: T;
  onClick: () => void;
  disabled?: boolean;
  value?: string;
  description?: ReactNode;
  hoverRightButton?: ReactNode;
  hint?: string;
  className?: string;
  labelCurrent?: ReactNode;
}

export interface DropdownMenuProps {
  items: DropdownMenuItem[];
  button: ReactNode;
  className?: string;
  itemClassName?: string;
  containerClassName?: string;
  selectedValue?: string;
  loading?: boolean;
  disabled?: boolean;
}

export const DropdownMenu = ({
  items,
  button,
  className,
  itemClassName,
  containerClassName,
  selectedValue,
  loading = false,
  disabled = false,
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const resolvedItems = useMemo(() => items.filter(Boolean), [items]);

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative flex items-center select-none',
        !containerClassName?.includes('w-') && 'w-full',
        containerClassName,
      )}
    >
      <button
        type="button"
        className="w-full flex items-center justify-center cursor-pointer disabled:cursor-default"
        onClick={event => {
          event.stopPropagation();
          if (!disabled && !loading) {
            setOpen(current => !current);
          }
        }}
        disabled={disabled || loading}
      >
        {button}
      </button>

      {open && !disabled && !loading && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-2 space-y-1 rounded border border-gray-200 bg-white py-[14px] shadow-lg',
            !className?.match(/(^|\s)w-\S*/) && 'w-full',
            className,
          )}
          role="menu"
        >
          {resolvedItems.map(
            (
              {
                label,
                onClick,
                disabled: itemDisabled,
                icon,
                description,
                value,
                hoverRightButton,
                className: itemOwnClassName,
              },
              index,
            ) => (
              <button
                key={`${value || index}`}
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (itemDisabled) {
                    return;
                  }
                  onClick();
                  setOpen(false);
                }}
                className={cn(
                  'group/button w-full flex flex-col items-start text-left text-[14px] leading-[20px] text-gray-900',
                  !itemClassName?.includes('px-') && !itemOwnClassName?.includes('px-') && 'px-4',
                  !itemClassName?.includes('py-') &&
                    !itemOwnClassName?.includes('py-') &&
                    'py-[6px]',
                  selectedValue && selectedValue === value && 'bg-gray-900/[.12]',
                  itemDisabled && 'text-gray-400',
                  itemClassName,
                  itemOwnClassName,
                )}
                disabled={itemDisabled}
              >
                <div className="w-full flex items-center gap-1.5">
                  {icon && <img src={icon} alt="" className="w-6 h-6 min-w-6 min-h-6 mr-1" />}
                  <div className="font-[Inter] text-[14px] leading-[20px] font-medium w-full">
                    {label}
                  </div>
                  {selectedValue && selectedValue === value ? (
                    <img src={checkIcon} alt="active" />
                  ) : (
                    <span />
                  )}
                  {hoverRightButton && (
                    <span className="opacity-0 group-hover/button:opacity-100">
                      {hoverRightButton}
                    </span>
                  )}
                </div>
                {description && description}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
};
